import React from 'react';
import { colors } from '@/assets/styles/colors';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { IconScout } from '@/components/ui/images/IconScout';
import { Text } from '@/components/ui/text/Text';
import { Spinner } from '@/components/ui/loading';
import Divider from '@/components/ui/separators/Divider';
import { Tooltip } from '@/components/ui/tooltips';
import { useTruncatedTooltip } from '@/hooks/ui';


export interface ListPanelProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  keyExtractor: (item: T) => string | number;
  isLoading: boolean;
  searchValue: string;
  onSearch: (text: string) => void;
  searchPlaceholder?: string;
  searchLabel?: string;
  title?: string;
  sentinelRef: React.RefCallback<HTMLDivElement>;
  scrollContainerRef: React.RefCallback<HTMLDivElement>;
  maxHeight?: string;
  'data-testid'?: string;
}

interface ListPanelItemProps {
  title: string;
  subtitle?: string | null;
  isSelected: boolean;
  onClick: () => void;
  'data-testid'?: string;
}

function ListPanelItem({ title, subtitle, isSelected, onClick, 'data-testid': dataTestId }: ListPanelItemProps) {
  const { ref, showTooltip, isTruncated, tooltipId, handleMouseEnter, handleMouseLeave } =
    useTruncatedTooltip('truncate', subtitle, 0);

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`w-full text-left py-1.5 rounded transition-colors ${isSelected ? '' : 'hover:bg-gray-100'}`}
        style={isSelected ? { backgroundColor: colors.primary.dash } : undefined}
        data-testid={dataTestId}
      >
        <Text
          variant="subheader-regular"
          color={isSelected ? colors.grays.neutralFF : colors.primary.main}
        >
          {title}
        </Text>
        {subtitle && (
          <div
            ref={ref}
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Text
              variant="small-regular"
              color={isSelected ? colors.grays.neutralFF : colors.grays.neutral66}
            >
              {subtitle}
            </Text>
            {showTooltip && isTruncated && (
              <Tooltip id={tooltipId} body={subtitle} anchorRef={ref} strategy="auto" />
            )}
          </div>
        )}
      </button>
    </li>
  );
}

export function ListPanel<T>({
  items,
  renderItem,
  keyExtractor,
  isLoading,
  searchValue,
  onSearch,
  searchPlaceholder,
  searchLabel = '\u00A0',
  title,
  sentinelRef,
  scrollContainerRef,
  maxHeight = '480px',
  'data-testid': dataTestId,
}: ListPanelProps<T>) {
  return (
    <div className="flex flex-col gap-6 w-80 shrink-0" data-testid={dataTestId}>
      <LabelInput
        label={searchLabel}
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={onSearch}
        iconRight={<IconScout name="search" color={colors.primary.auxiliar} size="lg" />}
        type="search"
        data-testid={dataTestId ? `${dataTestId}-search` : undefined}
      />

      {isLoading && items.length === 0 ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {title && (
            <>
              <Text variant="body-medium" color={colors.primary.dash}>
                {title}
              </Text>
              <Divider />
            </>
          )}
          <div
            ref={scrollContainerRef}
            className="overflow-y-auto flex flex-col"
            style={{ maxHeight }}
          >
            <ul
              className="flex flex-col gap-1"
              data-testid={dataTestId ? `${dataTestId}-list` : undefined}
            >
              {items.map((item) => (
                <React.Fragment key={keyExtractor(item)}>
                  {renderItem(item)}
                </React.Fragment>
              ))}
            </ul>
            <div ref={sentinelRef} className="h-1 shrink-0" />
            {isLoading && (
              <div className="flex justify-center py-2">
                <Spinner size="sm" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ListPanel;

ListPanel.Item = ListPanelItem;
